import { useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { cn } from "../../lib/cn";

const ALIGN = { left: "text-left", right: "text-right tabular", center: "text-center" } as const;

export function Table({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("overflow-x-auto", className)}>
      <table className="w-full border-collapse text-sm">{children}</table>
    </div>
  );
}
export function Th({ children, className, align = "left" }: { children?: ReactNode; className?: string; align?: "left" | "right" | "center" }) {
  return <th className={cn("border-b border-border px-3 py-2 text-xs font-medium text-fg-3", ALIGN[align], className)}>{children}</th>;
}
export function Td({ children, className, align = "left", mono }: { children?: ReactNode; className?: string; align?: "left" | "right" | "center"; mono?: boolean }) {
  return <td className={cn("border-b border-border/60 px-3 py-2 text-fg", ALIGN[align], mono && "font-mono text-xs", className)}>{children}</td>;
}

export interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  /** Sort accessor; omit to make column unsortable. */
  sortValue?: (row: T) => string | number | null;
  align?: "left" | "right" | "center";
  className?: string;
}

/**
 * Sortable grid with roving keyboard focus: ↑/↓ move between rows, Enter activates,
 * Home/End jump. Header buttons toggle sort and expose aria-sort.
 */
export function DataGrid<T>({
  rows,
  columns,
  rowKey,
  onRowActivate,
  initialSort,
  empty,
  caption,
  dense,
}: {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  onRowActivate?: (row: T) => void;
  initialSort?: { key: string; dir: "asc" | "desc" };
  empty?: ReactNode;
  caption?: string;
  dense?: boolean;
}) {
  const [sort, setSort] = useState(initialSort ?? null);
  const [focusIdx, setFocusIdx] = useState(0);
  const bodyRef = useRef<HTMLTableSectionElement>(null);

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col?.sortValue) return rows;
    const acc = col.sortValue;
    return [...rows].sort((a, b) => {
      const va = acc(a);
      const vb = acc(b);
      if (va === vb) return 0;
      if (va === null) return 1;
      if (vb === null) return -1;
      const r = va < vb ? -1 : 1;
      return sort.dir === "asc" ? r : -r;
    });
  }, [rows, columns, sort]);

  const focusRow = (i: number) => {
    const clamped = Math.max(0, Math.min(sorted.length - 1, i));
    setFocusIdx(clamped);
    (bodyRef.current?.querySelectorAll<HTMLTableRowElement>("tr")[clamped])?.focus();
  };

  const onKey = (e: KeyboardEvent<HTMLTableRowElement>, i: number, row: T) => {
    if (e.key === "ArrowDown") (e.preventDefault(), focusRow(i + 1));
    else if (e.key === "ArrowUp") (e.preventDefault(), focusRow(i - 1));
    else if (e.key === "Home") (e.preventDefault(), focusRow(0));
    else if (e.key === "End") (e.preventDefault(), focusRow(sorted.length - 1));
    else if (e.key === "Enter" && onRowActivate) (e.preventDefault(), onRowActivate(row));
  };

  if (rows.length === 0 && empty) return <>{empty}</>;

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm" role="grid" aria-rowcount={sorted.length}>
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead>
          <tr>
            {columns.map((c) => {
              const active = sort?.key === c.key;
              const ariaSort = active ? (sort!.dir === "asc" ? "ascending" : "descending") : "none";
              return (
                <th key={c.key} aria-sort={c.sortValue ? ariaSort : undefined} className={cn("border-b border-border px-3 py-2 text-xs font-medium text-fg-3", c.align === "right" ? "text-right" : "text-left")}>
                  {c.sortValue ? (
                    <button
                      type="button"
                      className={cn("inline-flex items-center gap-1 hover:text-fg", active && "text-fg")}
                      onClick={() => setSort((s) => (s?.key === c.key ? { key: c.key, dir: s.dir === "asc" ? "desc" : "asc" } : { key: c.key, dir: "desc" }))}
                    >
                      {c.header}
                      {active ? sort!.dir === "asc" ? <ArrowUp className="size-3" /> : <ArrowDown className="size-3" /> : <ArrowUpDown className="size-3 opacity-40" />}
                    </button>
                  ) : (
                    c.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody ref={bodyRef}>
          {sorted.map((row, i) => (
            <tr
              key={rowKey(row)}
              tabIndex={i === focusIdx ? 0 : -1}
              onKeyDown={(e) => onKey(e, i, row)}
              onFocus={() => setFocusIdx(i)}
              onClick={onRowActivate ? () => onRowActivate(row) : undefined}
              className={cn("outline-none transition-colors focus-visible:bg-panel-hover", onRowActivate && "cursor-pointer hover:bg-panel-2")}
            >
              {columns.map((c) => (
                <td key={c.key} className={cn("border-b border-border/60 px-3 text-fg", dense ? "py-1.5" : "py-2.5", c.align === "right" && "text-right tabular", c.className)}>
                  {c.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
